/**
 * The keepr theme system.
 *
 * A theme is a curated palette; a mode is light or dark. Every color token
 * in the app is a function of the two, and this file is the one place the
 * colors are authored. Two consumers read it:
 *
 *   - scripts/build-themes.mjs, which writes src/css/themes.css (committed;
 *     a test re-runs the generator and byte-compares, so the CSS can never
 *     drift from this file).
 *   - src/config/antdTheme.js, which feeds the same values to antd's
 *     ConfigProvider so antd-internal styling matches the CSS exactly.
 *
 * Structure tokens (radii, control heights, the font stack) are NOT themed
 * and stay in src/css/tokens.css.
 *
 * Authoring rules:
 *   - Neutrals are systematic. A theme names one `hue` and a `satScale`,
 *     and the shared stop tables below give every neutral token its target
 *     luminance and its saturation. The stops were measured off the original
 *     keepr palette, so `keepr` at hue 240 / scale 1 reproduces it exactly.
 *   - Accents are curated, so they are written out as literal hex.
 *   - Status colors mean the same thing in every theme, so they are shared
 *     per mode and never themed. Red is red.
 *
 * Contrast is enforced by src/config/__tests__/themes.test.js, which walks
 * every theme x mode. Edit a value here and the test tells you if you broke
 * the text floor.
 */

// --- color math -------------------------------------------------------

const hex2 = (n) => Math.round(n).toString(16).padStart(2, '0');

/** hsl() -> #rrggbb. Hue in degrees, saturation and lightness in percent. */
export function hslHex(h, s, l) {
	const sat = Math.max(0, Math.min(100, s)) / 100;
	const lig = Math.max(0, Math.min(100, l)) / 100;
	const c = (1 - Math.abs((2 * lig) - 1)) * sat;
	const x = c * (1 - Math.abs((((h % 360) + 360) % 360 / 60) % 2 - 1));
	const m = lig - (c / 2);
	const sector = Math.floor((((h % 360) + 360) % 360) / 60) % 6;
	const [r, g, b] = [[c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x]][sector];
	return `#${hex2((r + m) * 255)}${hex2((g + m) * 255)}${hex2((b + m) * 255)}`;
}

/** #rrggbb -> [r, g, b]. */
export function hexRgb(value) {
	const match = /^#?([0-9a-f]{6})$/i.exec(String(value).trim());
	if (!match) return null;
	const int = parseInt(match[1], 16);
	return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/** WCAG relative luminance of a hex color. */
export function luminance(value) {
	const rgb = hexRgb(value);
	if (!rgb) return 0;
	const channel = (v) => {
		const c = v / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};
	return (0.2126 * channel(rgb[0])) + (0.7152 * channel(rgb[1])) + (0.0722 * channel(rgb[2]));
}

/** WCAG contrast ratio between two hex colors, rounded to 2dp. */
export function contrast(a, b) {
	const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
	return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

const rgba = (value, alpha) => {
	const rgb = hexRgb(value);
	return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : null;
};

// --- neutral stops ----------------------------------------------------

/**
 * The hex at a hue and saturation whose relative luminance is closest to
 * `target`. Luminance rises monotonically with HSL lightness at a fixed hue
 * and saturation, so a bisection finds the crossing; the sweep afterwards
 * settles which of the two neighbouring 8-bit colors is actually nearer,
 * because the crossing lands between representable values.
 */
function hexAtLuminance(hue, sat, target) {
	let lo = 0;
	let hi = 100;
	for (let i = 0; i < 24; i += 1) {
		const mid = (lo + hi) / 2;
		if (luminance(hslHex(hue, sat, mid)) < target) lo = mid;
		else hi = mid;
	}
	const centre = (lo + hi) / 2;
	let best = null;
	let bestDelta = Infinity;
	for (let step = -0.8; step <= 0.8001; step += 0.1) {
		const candidate = hslHex(hue, sat, Math.max(0, Math.min(100, centre + step)));
		const delta = Math.abs(luminance(candidate) - target);
		if (delta < bestDelta) {
			bestDelta = delta;
			best = candidate;
		}
	}
	return best;
}

/**
 * Every neutral token as a target LUMINANCE, not a lightness.
 *
 * This is the reason contrast holds across themes. Two colors at the same
 * HSL lightness but different hues are not equally light to the eye or to
 * WCAG — a green at 44% lightness is measurably brighter than a violet at
 * 44%. Pinning lightness per token made moss's secondary text 4.35:1 while
 * keepr's was 4.71:1. Pinning luminance instead means `--ink-muted` sits at
 * exactly 5.26:1 on white in all six themes, and the saturation below only
 * decides how much hue shows at that fixed brightness.
 *
 * The light targets are the original keepr palette measured back, so the
 * default theme still renders the exact hexes the standards document names.
 */
const NEUTRAL_STOPS = {
	light: {
		'--surface': { lum: 1, s: 0 },
		'--surface-raised': { lum: 1, s: 0 },
		'--field-bg': { lum: 1, s: 0 },
		'--rail-bg': { lum: 0.9566, s: 11.1 },
		'--ground': { lum: 0.9314, s: 14.3 },
		'--hover-bg': { lum: 0.9314, s: 14.3 },
		'--chip-bg': { lum: 0.8897, s: 13 },
		'--line-soft': { lum: 0.8732, s: 11.1 },
		'--chip-bg-active': { lum: 0.8655, s: 14.3 },
		'--border': { lum: 0.7936, s: 8.7 },
		'--border-btn': { lum: 0.7633, s: 9.4 },
		'--border-control': { lum: 0.6761, s: 9.3 },
		// The stacked collection tile's layer edge: a shape, so 3:1 on every
		// ground a tile sits on (surface down to chip-bg), whatever the
		// collection's own colour. Darker than --decor, never text.
		'--tile-stack-edge': { lum: 0.26, s: 6 },
		// Loading skeletons (docs/platform-standards.md §13). A block of base
		// colour is a placeholder shape; the highlight is the shimmer's crest,
		// lighter than the base and still apart from the surface.
		'--skeleton-base': { lum: 0.84, s: 10 },
		'--skeleton-highlight': { lum: 0.92, s: 12 },
		// The video player (KPR-1922; platform-standards §19). The bezel is the
		// phone-shaped frame a vertical clip sits in: a shape, so 3:1 on the
		// ground and the surfaces it is drawn on. The well is the screen behind
		// the picture (letterbox, poster edges, the native controls' backdrop):
		// near-black in both modes, so white captions read on it.
		'--media-bezel': { lum: 0.02, s: 8 },
		'--media-well': { lum: 0.004, s: 6 },
		'--decor': { lum: 0.327, s: 5.8 },
		'--ink-muted': { lum: 0.1495, s: 4.9 },
		'--ink-mid': { lum: 0.0448, s: 7.1 },
		'--ink': { lum: 0.0087, s: 8 },
		// Chart neutrals (the data palette, below). "Other" and de-emphasis
		// are data, so 3:1 on the surface like any mark, nearly untinted, and
		// darker (light) or lighter (dark) than the colors so it is told from
		// every one of them; the diverging midpoint is "no difference", so it
		// sits just off the surface.
		'--data-other': { lum: 0.16, s: 2 },
		'--data-div-mid': { lum: 0.8, s: 6 },
	},
	// Dark is not an inversion. Surfaces climb as they rise — ground is the
	// floor, a popover the ceiling — and the ink stops stop short of white,
	// which halates on a dark ground.
	dark: {
		'--ground': { lum: 0.0057, s: 10 },
		'--rail-bg': { lum: 0.0082, s: 10 },
		'--surface': { lum: 0.0112, s: 9 },
		'--field-bg': { lum: 0.0149, s: 9 },
		'--surface-raised': { lum: 0.0181, s: 9 },
		'--hover-bg': { lum: 0.0207, s: 8 },
		'--chip-bg': { lum: 0.0248, s: 8 },
		'--line-soft': { lum: 0.0279, s: 7 },
		'--chip-bg-active': { lum: 0.0365, s: 8 },
		'--border': { lum: 0.0418, s: 7 },
		'--border-btn': { lum: 0.0508, s: 7 },
		'--border-control': { lum: 0.0701, s: 7 },
		'--tile-stack-edge': { lum: 0.19, s: 6 },
		'--skeleton-base': { lum: 0.0365, s: 8 },
		'--skeleton-highlight': { lum: 0.058, s: 8 },
		// Dark: the bezel climbs above the surfaces instead of sinking below them.
		'--media-bezel': { lum: 0.16, s: 6 },
		'--media-well': { lum: 0.002, s: 6 },
		'--decor': { lum: 0.15, s: 6 },
		'--ink-muted': { lum: 0.3459, s: 6 },
		'--ink-mid': { lum: 0.5814, s: 7 },
		'--ink': { lum: 0.8166, s: 9 },
		'--data-other': { lum: 0.28, s: 2 },
		'--data-div-mid': { lum: 0.045, s: 6 },
	},
};

// --- status ------------------------------------------------------------

// Status reads the same in every theme: a themed red would be a lie about
// severity. Five tokens each — the color, its hover, a tint to sit on, the
// tint's border, and the text color that goes on the tint.
const STATUS = {
	light: {
		'--danger': '#cf1322',
		'--danger-hover': '#a8071a',
		'--danger-tint': '#fff1f0',
		'--danger-border': '#ffa39e',
		'--danger-ink': '#a8071a',
		'--warning': '#c2600a',
		'--warning-hover': '#a04d05',
		'--warning-tint': '#fff8e6',
		'--warning-border': '#ffd97d',
		'--warning-ink': '#8a4405',
		'--success': '#0e7a48',
		'--success-hover': '#0a6039',
		'--success-tint': '#e7f5ed',
		'--success-border': '#9ad4b6',
		'--success-ink': '#0a6039',
		'--info': '#1668dc',
		'--info-hover': '#0f52b3',
		'--info-tint': '#e9f2ff',
		'--info-border': '#a3c8f7',
		'--info-ink': '#0f52b3',
	},
	dark: {
		'--danger': '#ff7875',
		'--danger-hover': '#ff9c99',
		'--danger-tint': '#2c1517',
		'--danger-border': '#5c2a2c',
		'--danger-ink': '#ff9c99',
		'--warning': '#f0a13c',
		'--warning-hover': '#f5b863',
		'--warning-tint': '#2b2012',
		'--warning-border': '#5a4320',
		'--warning-ink': '#f5b863',
		'--success': '#4ec38c',
		'--success-hover': '#6fd2a3',
		'--success-tint': '#12271d',
		'--success-border': '#224c38',
		'--success-ink': '#6fd2a3',
		'--info': '#5aa9ff',
		'--info-hover': '#7dbcff',
		'--info-tint': '#101f31',
		'--info-border': '#234a70',
		'--info-ink': '#7dbcff',
	},
};

// --- elevation ---------------------------------------------------------

// Cards carry a border, not a shadow. These are for frames that float:
// sheets, popovers, the palette. Dark leans on a near-black shadow plus the
// lifted --surface-raised, since a dark ground shows no soft grey halo.
const SHADOW = {
	light: '0 1px 3px rgba(20, 20, 30, 0.06), 0 12px 32px -12px rgba(20, 20, 30, 0.14)',
	dark: '0 1px 3px rgba(0, 0, 0, 0.5), 0 14px 36px -12px rgba(0, 0, 0, 0.7)',
};

// The upward shadow under a sticky footer bar (the phone action row). Light
// enough that it reads as a lift rather than a second border; in dark the
// separation comes from the bar's own border, so this only deepens it.
const SHADOW_LIFT = {
	light: '0 -2px 8px rgba(20, 20, 30, 0.06)',
	dark: '0 -2px 10px rgba(0, 0, 0, 0.45)',
};

const SCRIM = { light: 'rgba(20, 20, 30, 0.45)', dark: 'rgba(0, 0, 0, 0.66)' };

// --- the data palette ------------------------------------------------------

/**
 * The colors charts draw data in (docs/platform-standards.md §12). They are
 * not the accent and not status: a chart never paints a series in --action,
 * and good, warning and critical keep their meaning. Four jobs, four sets:
 *
 *   --data-1 … --data-6   categorical: one color for each value a chart is
 *                         split by, assigned in the theme's order by the
 *                         value's identity (a choice's position in its list),
 *                         never by its rank.
 *   --data-other          the neutral "Other" and de-emphasis grey (a stop in
 *                         NEUTRAL_STOPS).
 *   --data-seq-1 … 7      sequential: how much. One hue; step 1 sits nearest
 *                         the surface and step 7 farthest from it, so the
 *                         ramp runs light to dark in light mode and flips in
 *                         dark mode, where the strong end is the bright one.
 *   --data-div-neg / -mid / -pos
 *                         diverging: which side of a goal. Two of the theme's
 *                         own categorical colors, off the good-and-bad hues,
 *                         and a grey midpoint (a neutral stop).
 *
 * How the values were chosen — computed, never eyeballed. The checks are
 * src/utils/color/paletteChecks.js, and src/config/__tests__/themes.test.js
 * holds every theme × mode to them against the chart surface (--surface).
 * Every categorical color, in both modes:
 *
 *   - sits in the lightness band (OKLCH L 0.43–0.77 light, 0.48–0.67 dark),
 *     over the chroma floor (0.10), at 3:1 or more on the surface;
 *   - stays ΔE 15 from the accent (ΔE 25 for slot 1, every single-series
 *     chart's color) and ΔE 15 from each of --danger, --warning, --success
 *     and --info;
 *   - is ΔE 8 from its neighbours under protan and deutan vision and ΔE 15
 *     under normal vision; the first three slots are that far from each
 *     other; any two slots are ΔE 11 apart;
 *   - closes every donut ring (slot n against slot 1, n = 3 to 6: ΔE 6 under
 *     protan and deutan vision, 15 under normal vision);
 *   - is told from Other the same way (ΔE 6 and 15).
 *
 * The status floor is what shapes the palette. In light mode 3:1 on white
 * caps lightness near OKLCH 0.67, and the four status colors hold the mid
 * reds, oranges, golds, greens and blues, so what remains is azure, lime,
 * violets, purples, pinks and a deep brown. Each theme's hues and steps were
 * found by a depth-first search over those families (each at ±10° of hue,
 * lightness in 0.01 steps, three chroma levels) under all the checks at once,
 * keeping the order with the most margin. Six is the largest count found to
 * pass in every theme: Keepr and Graphite can carry seven, but a bounded
 * search (up to 40 million nodes per theme) found no seventh for Indigo,
 * Moss, Ember or Tide, whose accents take one or two of the families. The
 * series cap follows: six.
 *
 * Measured 2026-09-29, the worst of the twelve pairs against each floor:
 * neighbours CVD ΔE 9.5 (8) and normal 15.4 (15); the first three CVD 8.4
 * (8); any two slots 11.5 (11); donut ring CVD 6.7 (6) and normal 15.5 (15);
 * Other CVD 6.4 (6) and normal 15.2 (15); contrast 3.05:1 (3); accent 15.4
 * (15), slot 1 26.0 (25), ramp steps 17.5 (15); status 15.3 (15); poles CVD
 * 8.8 (8), 32° from a status hue (30). Tritan, reported and never gated,
 * bottoms out at 3.3. The margins are thin by construction: the search keeps
 * the order with the most margin, and there is not much to keep.
 *
 * To change a value: edit it here, run `npm run build:themes`, and let the
 * test say what broke. To add a theme: give it `data` and run the test.
 */

/** How many categorical colors every theme has: the chart series cap. */
export const DATA_SERIES = 6;

/** The token names of the data palette, by job, in order. */
export const DATA_TOKENS = Object.freeze({
	categorical: Object.freeze(Array.from({ length: DATA_SERIES }, (_, i) => `--data-${i + 1}`)),
	other: '--data-other',
	sequential: Object.freeze(Array.from({ length: 7 }, (_, i) => `--data-seq-${i + 1}`)),
	diverging: Object.freeze(['--data-div-neg', '--data-div-mid', '--data-div-pos']),
});

/** The token a chart is drawn on, which every data color is measured against. */
export const DATA_SURFACE = '--surface';

// Sequential ramps: one OKLCH hue at seven even lightnesses, step 1 nearest
// the surface. A theme uses blue, or orange where blue sits within ΔE 15 of
// its accent at some step (Indigo, Tide).
const DATA_RAMPS = {
	blue: {
		light: ['#d8eafe', '#aad2fd', '#7ab9fc', '#439ffa', '#2486df', '#0f6ebd', '#095798'],
		dark: ['#033663', '#074a83', '#0b5fa6', '#1075c9', '#2d8ce5', '#4da4fb', '#80bcfc'],
	},
	orange: {
		light: ['#fee1d6', '#fdbca6', '#fc9570', '#ec7348', '#d05a2e', '#b4410d', '#903208'],
		dark: ['#5d1d03', '#7d2a06', '#9d380a', '#bd4919', '#d76034', '#f1784d', '#fc9a77'],
	},
};

/** The names of a theme's categorical hues, in slot order. Names only describe. */
export function dataHues(themeKey) {
	return getTheme(themeKey).data.hues.slice(0, DATA_SERIES);
}

// --- the themes --------------------------------------------------------

/**
 * Six palettes, one per hue family, so no two read as versions of each
 * other: brand pink, no hue at all, blue-violet, green, orange, and cyan.
 *
 *   hue      — the neutral hue. Everything that is not accent or status
 *              carries a trace of it, which is what makes ember's greys
 *              read warm and tide's read cool.
 *   satScale — how much of that hue shows in the neutrals. 1 is the keepr
 *              original; below 1 approaches true grey, above 1 tints more.
 *   accent   — curated per mode. In light the accent is dark enough to be
 *              link text on white AND to carry white fill text; in dark
 *              that is arithmetically impossible, so the accent is bright
 *              and `--on-action` goes near-black on top of it.
 *   data     — the data palette (above): the categorical hues' names and
 *              their colors per mode, in slot order; the sequential ramp's
 *              hue; and the two diverging poles, below the goal then above
 *              it, named from the theme's own hues.
 */
export const THEMES = [
	{
		key: 'keepr',
		label: 'Keepr',
		blurb: 'The brand pink on cool grey. The original.',
		hue: 240,
		satScale: 1,
		accent: {
			light: {
				'--action': '#ec1a81',
				'--action-hover': '#c4185f',
				'--action-deep': '#b8115f',
				'--action-tint': '#fdebf4',
				'--action-tint-soft': '#fdf2f8',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#f852a0',
				'--action-hover': '#fb76b5',
				'--action-deep': '#fb8ac1',
				'--action-tint': '#3a1327',
				'--action-tint-soft': '#2a0e1d',
				'--on-action': '#1a0a12',
			},
		},
		data: {
			hues: ['azure', 'lime', 'violet', 'pink', 'purple', 'brown'],
			light: ['#15a1bb', '#49a718', '#5a369e', '#920956', '#a849bd', '#843d06'],
			dark: ['#0c739f', '#37850c', '#7b4fd0', '#aa4a74', '#a772f5', '#995725'],
			ramp: 'blue',
			diverging: ['purple', 'azure'],
		},
	},
	{
		key: 'graphite',
		label: 'Graphite',
		blurb: 'No hue at all. Ink is the accent; the data is the color.',
		hue: 230,
		satScale: 0.45,
		accent: {
			light: {
				'--action': '#23232b',
				'--action-hover': '#3c3c47',
				'--action-deep': '#23232b',
				'--action-tint': '#eeeef2',
				'--action-tint-soft': '#f5f5f8',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#e4e4ec',
				'--action-hover': '#f3f3f8',
				'--action-deep': '#eeeef4',
				'--action-tint': '#2c2c34',
				'--action-tint-soft': '#232329',
				'--on-action': '#121216',
			},
		},
		data: {
			hues: ['azure', 'lime', 'violet', 'pink', 'purple', 'brown'],
			light: ['#15a1bb', '#49a718', '#5a369e', '#920956', '#a849bd', '#7c3e05'],
			dark: ['#0c739f', '#4b810b', '#7766e9', '#bd2d76', '#bb65df', '#995725'],
			ramp: 'blue',
			diverging: ['purple', 'azure'],
		},
	},
	{
		key: 'indigo',
		label: 'Indigo',
		blurb: 'Blue-violet on cool slate. Quiet and technical.',
		hue: 245,
		satScale: 1.5,
		accent: {
			light: {
				'--action': '#4f46e5',
				'--action-hover': '#4338ca',
				'--action-deep': '#3a32ad',
				'--action-tint': '#ecebfe',
				'--action-tint-soft': '#f4f3ff',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#8b88f8',
				'--action-hover': '#a5a3fb',
				'--action-deep': '#b4b2fc',
				'--action-tint': '#23224c',
				'--action-tint-soft': '#1a1a3a',
				'--on-action': '#0d0d1c',
			},
		},
		data: {
			hues: ['lime', 'azure', 'pink', 'purple', 'brown', 'orchid'],
			light: ['#899c12', '#159ecf', '#920956', '#ad78fc', '#843d06', '#a63ba8'],
			dark: ['#67920f', '#0c739f', '#c32a61', '#7d51bb', '#995725', '#c956bd'],
			ramp: 'orange',
			diverging: ['lime', 'azure'],
		},
	},
	{
		key: 'moss',
		label: 'Moss',
		blurb: 'Deep green on a warm grey. Easy for long sessions.',
		hue: 150,
		satScale: 0.9,
		accent: {
			light: {
				'--action': '#1c7a4c',
				'--action-hover': '#15613c',
				'--action-deep': '#125234',
				'--action-tint': '#e3f3ea',
				'--action-tint-soft': '#f1faf5',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#4cc389',
				'--action-hover': '#6fd3a2',
				'--action-deep': '#82dcb0',
				'--action-tint': '#153025',
				'--action-tint-soft': '#0f241b',
				'--on-action': '#06170e',
			},
		},
		data: {
			hues: ['violet', 'azure', 'pink', 'orchid', 'lime', 'magenta'],
			light: ['#5a369e', '#15a1bb', '#920956', '#a454cf', '#899c12', '#d650a5'],
			dark: ['#7766e9', '#0c739f', '#c42a56', '#d45fc6', '#45770a', '#a939a3'],
			ramp: 'blue',
			diverging: ['magenta', 'violet'],
		},
	},
	{
		key: 'ember',
		label: 'Ember',
		blurb: 'Burnt orange on warm sand. The one with a temperature.',
		hue: 28,
		satScale: 1.6,
		accent: {
			light: {
				'--action': '#c2410c',
				'--action-hover': '#9d3409',
				'--action-deep': '#8a2e08',
				'--action-tint': '#fdeee5',
				'--action-tint-soft': '#fef6f1',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#fb8b4c',
				'--action-hover': '#fda370',
				'--action-deep': '#ffb188',
				'--action-tint': '#3a2114',
				'--action-tint-soft': '#2a180e',
				'--on-action': '#1c0d05',
			},
		},
		data: {
			hues: ['violet', 'azure', 'pink', 'orchid', 'lime', 'magenta'],
			light: ['#5a369e', '#159eb7', '#920956', '#a454cf', '#899c12', '#d650a5'],
			dark: ['#7766e9', '#0e7ba1', '#c42a56', '#d45fc6', '#45770a', '#a939a3'],
			ramp: 'blue',
			diverging: ['magenta', 'violet'],
		},
	},
	{
		key: 'tide',
		label: 'Tide',
		blurb: 'Cyan-teal on blue-grey. Cold, bright, high contrast.',
		hue: 205,
		satScale: 1.4,
		accent: {
			light: {
				'--action': '#0e7490',
				'--action-hover': '#0b5e75',
				'--action-deep': '#0a5265',
				'--action-tint': '#e1f2f8',
				'--action-tint-soft': '#f0f9fb',
				'--on-action': '#ffffff',
			},
			dark: {
				'--action': '#38bfd8',
				'--action-hover': '#5ccfe4',
				'--action-deep': '#74d8ea',
				'--action-tint': '#0f323d',
				'--action-tint-soft': '#0a252e',
				'--on-action': '#03161b',
			},
		},
		data: {
			hues: ['pink', 'purple', 'violet', 'magenta', 'lime', 'orchid'],
			light: ['#920956', '#a772f5', '#5a369e', '#d650a5', '#899c12', '#9e44bb'],
			dark: ['#c42a56', '#a772f5', '#525ad8', '#d75ec1', '#3e920e', '#9444be'],
			ramp: 'orange',
			diverging: ['magenta', 'violet'],
		},
	},
];

export const THEME_KEYS = THEMES.map((t) => t.key);
export const DEFAULT_THEME = 'keepr';

export const APPEARANCES = ['system', 'light', 'dark'];
export const DEFAULT_APPEARANCE = 'system';
export const MODES = ['light', 'dark'];

export const getTheme = (key) => THEMES.find((t) => t.key === key) || THEMES[0];

/**
 * Every color token for one theme in one mode, as a { '--token': value } map.
 * The only place a color is assembled; CSS and antd both read this.
 */
export function buildPalette(themeKey, mode) {
	const theme = getTheme(themeKey);
	const resolved = mode === 'dark' ? 'dark' : 'light';
	const vars = {};

	for (const [token, stop] of Object.entries(NEUTRAL_STOPS[resolved])) {
		vars[token] = hexAtLuminance(theme.hue, stop.s * theme.satScale, stop.lum);
	}
	// Text on an ink-filled surface (the selected segment, a solid pill).
	// Ink is near-black in light and near-white in dark, so this flips.
	vars['--on-ink'] = resolved === 'dark' ? vars['--ground'] : '#ffffff';

	Object.assign(vars, theme.accent[resolved]);
	// The input focus glow. Dark grounds swallow a 12% wash, so it doubles.
	vars['--action-ring'] = rgba(vars['--action'], resolved === 'dark' ? 0.28 : 0.12);
	// A halo the accent casts, not a wash it sits behind: the phone's floating
	// action button. Far stronger than the focus ring, and never interchangeable
	// with it — a focus ring at this weight would shout on every tab stop.
	vars['--action-glow'] = rgba(vars['--action'], resolved === 'dark' ? 0.4 : 0.5);

	Object.assign(vars, STATUS[resolved]);
	vars['--shadow-elevated'] = SHADOW[resolved];
	vars['--shadow-lift'] = SHADOW_LIFT[resolved];
	vars['--scrim'] = SCRIM[resolved];

	// The data palette. --data-other and --data-div-mid are neutral stops,
	// already set above with the rest of the ramp.
	const { data } = theme;
	data[resolved].slice(0, DATA_SERIES).forEach((hex, index) => {
		vars[DATA_TOKENS.categorical[index]] = hex;
	});
	DATA_RAMPS[data.ramp][resolved].forEach((hex, index) => {
		vars[DATA_TOKENS.sequential[index]] = hex;
	});
	const pole = (hue) => data[resolved][data.hues.indexOf(hue)];
	vars['--data-div-neg'] = pole(data.diverging[0]);
	vars['--data-div-pos'] = pole(data.diverging[1]);

	return vars;
}

/**
 * One theme's data palette in one mode, by job: what chart code reads when
 * it cannot use the CSS variables (echarts paints on a canvas, and needs the
 * values themselves).
 *
 *   { categorical: [DATA_SERIES hex], other, sequential: [7 hex], diverging: { neg, mid, pos } }
 */
export function buildDataPalette(themeKey, mode) {
	const p = buildPalette(themeKey, mode);
	return {
		categorical: DATA_TOKENS.categorical.map((token) => p[token]),
		other: p[DATA_TOKENS.other],
		sequential: DATA_TOKENS.sequential.map((token) => p[token]),
		diverging: { neg: p['--data-div-neg'], mid: p['--data-div-mid'], pos: p['--data-div-pos'] },
	};
}
